import { useState } from 'react';
import { CalendarCheck2, ChevronDown, AlertTriangle, Loader2 } from 'lucide-react';
import type { TaskWithRelations, SimulateForTaskResponse } from '@vagusx/clients';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  ConfidenceBadge,
  RecommendationReasons,
  buildWinnerSummary,
  normalizeRecommendation,
} from '@/components/custom/appointment-type-recommendation';
import { useSimulateAppointmentTypeForTask } from '@/hooks/use-appointment-type-matching';

interface ReferralAppointmentTypeCardProps {
  task: TaskWithRelations;
}

/**
 * Preview, on the Tasks Overview, of which appointment type the matcher would
 * pick for this referral — shown above the "Call to schedule" button so the
 * operator can sanity-check the selection before starting a call. Read-only:
 * the type, a confidence badge, and an expandable "Why this type?" that reuses
 * the simulator's reason breakdown.
 *
 * Renders nothing for tasks that can't resolve a referral document (same gate
 * as the call button: needs a `document`/`email` source).
 */
export function ReferralAppointmentTypeCard({
  task,
}: ReferralAppointmentTypeCardProps): React.ReactElement | null {
  const couldResolveDocument =
    !!task.objectId && (task.objectType === 'document' || task.objectType === 'email');

  const { data, isLoading, isError } = useSimulateAppointmentTypeForTask(
    task.id,
    couldResolveDocument
  );

  if (!couldResolveDocument) return null;

  if (isLoading) {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Matching appointment type…
        </div>
      </Shell>
    );
  }

  // Soft-fail: the preview is advisory, so a failed match shouldn't block the
  // call action below it. Show a quiet note instead of an error toast.
  if (isError || !data) {
    return (
      <Shell>
        <p className="text-sm text-muted-foreground">Couldn't preview the appointment type.</p>
      </Shell>
    );
  }

  if (!data.hasDocument) {
    return (
      <Shell>
        <p className="text-sm text-muted-foreground">
          No referral document linked yet — the appointment type will be matched once extraction
          completes.
        </p>
      </Shell>
    );
  }

  return <RecommendationBody data={data} />;
}

function RecommendationBody({ data }: { data: SimulateForTaskResponse }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const { recommendation, signals } = data;
  const { effectiveResult } = normalizeRecommendation(recommendation);
  const winnerSummary = buildWinnerSummary(effectiveResult);

  const tone = effectiveResult.isTie ? 'tie' : effectiveResult.appointmentTypeId ? 'match' : 'none';

  return (
    <Shell>
      <div className="flex flex-wrap items-center gap-2">
        <CalendarCheck2 className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Appointment type for this referral</span>
        {tone === 'match' && <ConfidenceBadge confidence={effectiveResult.confidence} />}
      </div>

      <div className="mt-1.5">
        {tone === 'match' ? (
          <p className="text-base font-semibold leading-snug">
            {effectiveResult.appointmentTypeName}
          </p>
        ) : tone === 'tie' ? (
          <p className="flex items-center gap-1.5 text-sm font-medium text-yellow-700">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Tie — {effectiveResult.tiedTypeIds.length} types scored equally
          </p>
        ) : (
          <p className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            No matching appointment type
          </p>
        )}
        {tone === 'match' && winnerSummary && (
          <p className="mt-0.5 text-sm text-muted-foreground">Selected because: {winnerSummary}.</p>
        )}
      </div>

      <SignalChips signals={signals} />

      <Collapsible open={open} onOpenChange={setOpen} className="mt-2">
        <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground">
          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
          {open ? 'Hide reasons' : 'Why this type?'}
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-3">
          <RecommendationReasons result={recommendation} />
        </CollapsibleContent>
      </Collapsible>
    </Shell>
  );
}

/** Small read-only chips showing the signals the match was run against. */
function SignalChips({
  signals,
}: {
  signals: SimulateForTaskResponse['signals'];
}): React.ReactElement | null {
  const chips: string[] = [];
  if (signals.cptCodes.length > 0) chips.push(`CPT: ${signals.cptCodes.join(', ')}`);
  if (signals.icdCodes.length > 0) chips.push(`ICD: ${signals.icdCodes.join(', ')}`);
  if (signals.patientType)
    chips.push(signals.patientType === 'new' ? 'New patient' : 'Existing patient');
  if (signals.isUrgent) chips.push('Urgent');
  if (chips.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {chips.map((chip) => (
        <Badge key={chip} variant="secondary" className="text-xs font-normal">
          {chip}
        </Badge>
      ))}
    </div>
  );
}

/** Consistent bordered container so every state lines up above the button. */
function Shell({ children }: { children: React.ReactNode }): React.ReactElement {
  return <div className="w-full rounded-lg border bg-muted/30 p-3">{children}</div>;
}
